// context/LanguageContext.js
import React, { createContext, useState, useContext } from 'react';

const translations = {
  en: {
    // Header
    headerTitle: 'Cheminformatics Lab (CCEN UFPB)',
    navResearch: 'Research',
    navTeam: 'Team',
    navTools: 'Tools',
    navContact: 'Contact',
    // Home
    homeTitle: 'Supporting Drug Discovery with Data Science and AI',
    homeIntro: `The Cheminformatics Lab at Federal University of Paraiba (UFPB) works
      at the intersection of pharmaceutical development, data science
      and Artificial Inteligence to accelerate the discovery of novel drugs.
      Our focus is split between development of open source, innovative
      computational methods and applications of such methods
      in tangible discovery campaigns.`,
    highlights: 'Highlights',
    qsarTitle: 'QSAR and Cheminformatics',
    qsarText: `We use machine learning and data science to investigate
      the relationship between chemicals and their various biological activities.
      We strive to not only make advancements in methodology, but to develop 
      robust workflows for using these tools.`,
    seeWhatWePublished: `See what we’ve published`,
    ourResourcesTitle: 'Our Resources',
    ourResourcesText: `Tools are useless if no one can use them. All of our tools 
      and code is freely available for anyone to utilize and build upon. 
      Further, many of our tools have easy to use web interfaces to 
      enhance accessibility for the public.`,
    browseOurTools: `Browse our tools`,
    ourTeamTitle: 'Our Team',
    ourTeamText: `We are a group of diverse researchers with a broad range of academic backgrounds. Our team is able to approach problems from multifaceted viewpoints to increase the robustness of our solutions.`,
    meetOurTeam: 'Meet Our Team',

    // Team
    teamLabMembersTitle: 'Lab Members',
    teamCurrentMembersBtn: 'Current Members',
    teamCollaboratorsBtn: 'Collaborators',
    teamAlumniBtn: 'Alumni',

    teamCurrentMembersTitle: 'Current Members',
    teamCurrentMembersText: `Our lab is a team of highly skilled interdisciplinary scientists 
    and scientists-in-training. Cheminformatics is a field that requires effective and 
    clear communication between a wide array experts in different areas, from biology 
    to synthetic chemistry. We work to approach our work from as many different 
    viewpoints as possible so that it is the most impact to our colleagues that utilize it.`,

    teamCollaboratorsTitle: 'Collaborators',
    teamCollaboratorsText: `As computer loving people, we don’t often get to see the insides 
    of chemical lab. Instead, in the pursuit safety, we leave this work up to out lovely 
    collaborators, both at UFPB and across the globe and experts in all various domains. 
    We also collaborate closely with several other computational groups and companies.`,

    teamAlumniTitle: 'Alumni',
    teamAlumniText: `Alumni from the UFPB have found success at all kinds of futures, 
    from academic to industrial. These are past lab members who have moved on for our 
    group to further innovate elsewhere`,

    // --- Tools Page ---
    toolsTitle: 'Tools',
    toolsIntro1: `As a lab, we release and periodically update all the tools we develop
      and use for our research. Our tools are open sourced, and we encourage you
      to reach out to us with any collaboration or issues. The goal is to produce
      high quality, clean tools that can be easily utilized by any research with
      minimal investment to learning how the tool works. Sometimes we offer a tool
      as a webserver for easier use, though standalone local version exist as well.`,
    toolsIntro2: `Our tools, curated databases, and webservers can be found below.
      If you use one of these please cite it! Our students and researchers work hard
      to make these tools available for easy use, give them a pat on the back.`,

    toolsPredictiveModelsTitle: 'Predictive Models',
    toolsAmiActiveTitle: 'AmiActive',
    toolsAmiActiveDesc: 'AmIActive is a QSAR-based web tool for biological activity prediction and target fishing. The AmIActive system uses QSAR models trained with biological activity data from the ChEMBL database and currently covers single proteins, protein complexes, protein families, cell lines, organisms, and tissues. The system contains 3,239 models corresponding to 2,277 distinct targets of interest. These models are Random Forest classifiers trained with circular fingerprint descriptors, and all models have clearly defined applicability domains.',
    toolsMolPredictxTitle: 'Molpredictx',
    toolsMolPredictxDesc: 'MolPredictX is a free-access web tool that enables the scientific community to predict the biological activities of chemical molecules. Utilizing internally developed Quantitative Structure-Activity Relationship (QSAR) models, MolPredictX offers 32 qualitative predictions (active or inactive) and quantitative probabilities of bioactivity against various targets',

    toolsDatabasesTitle: 'Databases',
    toolsSistematxTitle: 'SistematX',
    toolsSistematxDesc: 'SISTEMAT X Web is a suite of tools for managing a database of secondary metabolites, accessible to the scientific community through a web interface. It allows the management of species information, including taxonomic data (genus, tribe, family), physicochemical properties, and experimental data such as NMR spectra and biological activity.',

    researchTitle: 'Research Publications',
    selectedTitle: 'Selected Articles',
    booksTitle: 'Published Books',
    allPublicationsTitle: 'All Publications',
    doiButtonText: 'View DOI',
    placeholdersearchBox: 'Search...',
  },
  pt: {
    // Header
    headerTitle: 'Laboratório de Quimioinformática (CCEN UFPB)',
    navResearch: 'Pesquisa',
    navTeam: 'Equipe',
    navTools: 'Ferramentas',
    navContact: 'Contato',
    // Home
    homeTitle: 'Apoiando a Descoberta de Medicamentos com Ciência de Dados e IA',
    homeIntro: `O Laboratório de Quimioinformática da Universidade Federal da Paraíba (UFPB)
      trabalha na interseção do desenvolvimento farmacêutico, ciência de dados 
      e Inteligência Artificial para acelerar a descoberta de novos fármacos.
      Nosso foco se divide entre o desenvolvimento de métodos computacionais
      inovadores e open source, e a aplicação desses métodos em campanhas
      de descoberta tangíveis.`,
    highlights: 'Destaques',
    qsarTitle: 'QSAR e Quimioinformática',
    qsarText: `Nós utilizamos machine learning e ciência de dados para investigar
      a relação entre substâncias químicas e suas diversas atividades biológicas.
      Nosso objetivo não é apenas avançar em metodologia, mas desenvolver 
      fluxos de trabalho robustos para o uso dessas ferramentas.`,
    seeWhatWePublished: `Veja nossas publicações`,
    ourResourcesTitle: 'Nossos Recursos',
    ourResourcesText: `Ferramentas são inúteis se ninguém consegue usá-las.
      Todos os nossos códigos e ferramentas estão disponíveis gratuitamente
      para qualquer um utilizar e evoluir. Além disso, muitas delas contam 
      com interfaces web de fácil uso, aumentando a acessibilidade para o público.`,
    browseOurTools: `Veja nossas ferramentas`,
    ourTeamTitle: 'Nossa Equipe',
    ourTeamText: `Somos um grupo de pesquisadores diversos com uma ampla gama de formações acadêmicas. Nossa equipe é capaz de abordar problemas de pontos de vista multifacetados para aumentar a robustez de nossas soluções.`,
    meetOurTeam: 'Conheça a Equipe',

    // Team
    teamLabMembersTitle: 'Membros do Laboratório',
    teamCurrentMembersBtn: 'Integrantes Atuais',
    teamCollaboratorsBtn: 'Colaboradores',
    teamAlumniBtn: 'Egressos',

    teamCurrentMembersTitle: 'Integrantes Atuais',
    teamCurrentMembersText: `Nosso laboratório é composto por cientistas interdisciplinares 
    e em treinamento, altamente qualificados. Quimi-informática é uma área que exige 
    comunicação eficaz entre especialistas em diferentes campos, como biologia e química 
    sintética. Buscamos abordar nossos projetos de diversos pontos de vista para que 
    tenham o maior impacto possível para nossos colegas que utilizam as ferramentas.`,

    teamCollaboratorsTitle: 'Colaboradores',
    teamCollaboratorsText: `Como amantes de computadores, raramente vemos o interior de 
    um laboratório químico. Em nome da segurança, deixamos esse trabalho para nossos 
    queridos colaboradores, tanto na UFPB quanto ao redor do mundo, especialistas 
    em diversas áreas. Também colaboramos de perto com outros grupos computacionais 
    e empresas.`,

    teamAlumniTitle: 'Egressos',
    teamAlumniText: `Egressos da UFPB encontraram sucesso em diversos caminhos, 
    da academia à indústria. Estes são membros anteriores do laboratório que seguiram 
    adiante para inovar ainda mais em outros lugares.`,

    toolsTitle: 'Ferramentas',
    toolsIntro1: `Como um laboratório, lançamos e periodicamente atualizamos todas as
      ferramentas que desenvolvemos e utilizamos em nossa pesquisa. Nossas ferramentas
      são de código aberto, e encorajamos que você entre em contato conosco em caso de
      colaboração ou problemas. Nosso objetivo é produzir ferramentas limpas e de alta
      qualidade, que possam ser facilmente utilizadas por qualquer pesquisador com
      investimento mínimo para aprender como a ferramenta funciona. Às vezes oferecemos
      uma ferramenta como um servidor web para uso mais fácil, embora versões locais
      também existam.`,
    toolsIntro2: `Nossas ferramentas, bases de dados e servidores web estão listados
      abaixo. Se você usar alguma delas, por favor cite! Nossos alunos e pesquisadores
      trabalham duro para disponibilizar essas ferramentas de forma fácil, dê-lhes o
      devido reconhecimento.`,

    toolsPredictiveModelsTitle: 'Modelos Preditivos',
    toolsAmiActiveTitle: 'AmiActive',
    toolsAmiActiveDesc: 'AmIActive é uma ferramenta web baseada em QSAR para previsão de atividade biológica e identificação de alvos. O sistema AmIActive utiliza modelos QSAR treinados com dados de atividade biológica provenientes do banco de dados ChEMBL e atualmente abrange proteínas individuais, complexos proteicos, famílias de proteínas, linhagens celulares, organismos e tecidos. O sistema contém 3.239 modelos correspondentes a 2.277 alvos distintos de interesse. Esses modelos são classificadores Random Forest treinados com descritores de fingerprint circulares, e todos os modelos possuem domínios de aplicabilidade claramente definidos.',
    toolsMolPredictxTitle: 'Molpredictx',
    toolsMolPredictxDesc: 'O MolPredictX é uma ferramenta web de acesso gratuito que permite à comunidade científica prever atividades biológicas de moléculas químicas. Utilizando modelos de Relação Quantitativa Estrutura-Atividade (QSAR) desenvolvidos internamente, o MolPredictX oferece 32 predições qualitativas (ativo ou inativo) e probabilidades quantitativas de bioatividade contra diversos alvos',

    toolsDatabasesTitle: 'Bancos de Dados',
    toolsSistematxTitle: 'SistematX',
    toolsSistematxDesc: 'O SISTEMAT X Web é uma suíte de ferramentas para gerenciar um banco de dados de metabólitos secundários, acessível à comunidade científica por meio de uma interface web. Ele permite o gerenciamento de informações sobre espécies, incluindo dados taxonômicos (gênero, tribo, família), propriedades físico-químicas e dados experimentais, como espectros de RMN e atividade biológica.',

    researchTitle: 'Pesquisas e Publicações',
    selectedTitle: 'Artigos Selecionados',
    booksTitle: 'Livros Publicados',
    allPublicationsTitle: 'Todas as Publicações',
    doiButtonText: 'Ver DOI',
    placeholdersearchBox: 'Pesquisar...',
  },
};

export const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  // Estado que armazena o idioma atual. Padrão: 'en'
  const [language, setLanguage] = useState('en');

  // Função para trocar de idioma
  const switchLanguage = (lang) => {
    if (lang === 'en' || lang === 'pt') {
      setLanguage(lang);
    }
  };

  // Função de tradução simples que busca no dicionário
  const t = (key) => {
    return translations[language][key];
  };

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook personalizado para facilitar o uso do contexto em qualquer componente
export function useLanguage() {
  return useContext(LanguageContext);
}
